import type { FC, PropsWithChildren } from "react";
import React from "react";

import cn from "classnames";

import { TextCore } from "../../helpers/common/TextCore/TextCore";

import type { DividerDirection, DividerViews } from "./types";
import type {
    PropsWithCustomVarsStyles,
    PropsWithTsId,
    PropsWithViewNever,
} from "../../types/react";
import type { TextProps } from "../Text";

import classes from "./Divider.styl";

const DIVIDER_TEST_ID = "divider-test-id";
const DIVIDER_DISPLAY_NAME = "Divider";

export type IDividerProps = PropsWithViewNever<
    {
        /** Отвечает за направление линии разделителя. direction === "vertical" будет работать только при пустом label */
        direction?: DividerDirection;
        label?: string;
    },
    DividerViews
>;

export type DividerProps = PropsWithCustomVarsStyles<IDividerProps> &
    Partial<PropsWithTsId> &
    Omit<
        TextProps,
        | "truncateStyle"
        | "typographyStyle"
        | "maxLines"
        | "underline"
        | "strikethrough"
        | "alignmentX"
        | "lineHeight"
        | "quote"
        | "italic"
        | "asList"
    >;

/**
 * Компонент в виде линии, с помощью которого можно отделить одну часть контента от другой
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?node-id=2-23539&m=dev" target="_blank">Спека</a>
 * @constructor
 */
export const Divider: FC<PropsWithChildren<DividerProps>> = ({
    view = "primary",
    direction = "horizontal",
    label,
    "data-tsid": tsid,
    width,
    textCase,
    customColorsStyles,
    customVarsStyles,
}) => {
    const wrapperStyle = cn(
        classes["wrapper"],
        classes[`__view-${view}`],
        !label && direction === "vertical" && classes["__vertical"],
        direction === "horizontal" && classes["__horizontal"],
        customVarsStyles,
        customColorsStyles,
    );

    const dividerLine = cn(
        classes["line"],
        classes[`__direction-${direction}`],
    );

    return (
        <div
            className={wrapperStyle}
            data-tsid={tsid || DIVIDER_TEST_ID}
            data-uikit={DIVIDER_DISPLAY_NAME}
        >
            {label && (
                <div className={classes["label-wrapper"]}>
                    <div className={classes["line-half"]} />
                    {label ? (
                        <TextCore
                            maxLines={2}
                            truncateStyle="ellipsis"
                            textContainerClassName={classes["divider-label"]}
                            typographyStyle="caption-m"
                            alignmentX="center"
                            width={width}
                            textCase={textCase}
                        >
                            {label}
                        </TextCore>
                    ) : null}
                    <div className={classes["line-half"]} />
                </div>
            )}
            {!label && <div className={dividerLine} />}
        </div>
    );
};

Divider.displayName = DIVIDER_DISPLAY_NAME;

export default Divider;
